import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BookmarkSimple, ShoppingCartSimple, PlayCircle, BookOpen, ShareNetwork,
  WhatsappLogo, XLogo, FacebookLogo, LinkSimple, Check, Star, Eye, Stack, CaretDown, CaretRight,
  User as UserIcon, Buildings, Books, ListChecks, Clock, House, Info, ChatCircleText, SealCheck, PencilSimple,
} from "@phosphor-icons/react";
import MangaRatings from "@/components/MangaRatings";
import SeriesVolumes from "@/components/SeriesVolumes";
import { youtubeAutoplayEmbed } from "@/lib/youtube";
import Seo from "@/components/Seo";

export default function MangaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [manga, setManga] = useState(null);
  const [status, setStatus] = useState({ in_collection: false, in_wishlist: false, in_read: false });
  const [mangaStats, setMangaStats] = useState({ collection_count: 0, wishlist_count: 0, read_count: 0 });
  const [rating, setRating] = useState({ average: 0, count: 0 });
  const [series, setSeries] = useState(null);
  const [otherByAuthor, setOtherByAuthor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("volumes");
  const [listOpen, setListOpen] = useState(false);
  const [locked, setLocked] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLocked(null);
    (async () => {
      try {
        const { data } = await api.get(`/manga/${id}`);
        setManga(data);
        api.post(`/manga/${id}/view`).catch(() => {});
        api.get(`/manga/${id}/stats`).then(r => setMangaStats(r.data)).catch(() => {});
        api.get(`/manga/${id}/ratings`).then(r => setRating({ average: r.data.average, count: r.data.count })).catch(() => {});
        api.get(`/manga/${id}/series`).then(r => setSeries(r.data)).catch(() => {});
        if (data.author) {
          api.get(`/manga`, { params: { q: data.author } }).then(r => setOtherByAuthor(r.data.filter(m => m.manga_id !== data.manga_id).slice(0, 4))).catch(() => {});
        }
        if (user) {
          try { const s = await api.get(`/me/list-status/${id}`); setStatus(s.data); } catch (e) { /* */ }
        }
      } catch (e) {
        if (e.response?.status === 423) {
          setLocked(e.response.data?.detail || { locked: true });
        } else {
          toast.error("Mangá não encontrado");
          navigate("/database");
        }
      } finally { setLoading(false); }
    })();
  }, [id, user, navigate]);

  async function toggleList(listType) {
    if (!user) { toast.error("Entra para usar esta funcionalidade"); navigate("/login"); return; }
    const key = `in_${listType}`;
    const labels = {
      collection: ["Adicionado à coleção", "Removido da coleção"],
      wishlist: ["Guardado para mais tarde", "Removido de mais tarde"],
      read: ["Marcado como lido", "Removido dos lidos"],
    };
    try {
      if (status[key]) {
        await api.delete(`/me/list/${listType}/${id}`);
        setStatus({ ...status, [key]: false });
        toast.success(labels[listType][1]);
      } else {
        await api.post(`/me/list/${listType}/${id}`);
        setStatus({ ...status, [key]: true });
        toast.success(labels[listType][0]);
      }
      api.get(`/manga/${id}/stats`).then(r => setMangaStats(r.data)).catch(() => {});
      api.get(`/manga/${id}/series`).then(r => setSeries(r.data)).catch(() => {});
    } catch (err) {
      if (err.response?.status === 423) {
        const pa = err.response.data?.detail?.publish_at;
        toast.error(pa ? `Este mangá só será lançado a ${new Date(pa).toLocaleString("pt-PT")}` : "Este mangá ainda não foi lançado");
      } else {
        toast.error(formatError(err.response?.data?.detail));
      }
    }
    finally { setListOpen(false); }
  }

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (locked) return <LockedManga detail={locked} />;
  if (!manga) return null;

  const embed = youtubeAutoplayEmbed(manga.youtube_url);
  const totalVolumes = series && !series.single ? series.total : (manga.series_total || null);
  const commentCount = rating.count;
  const listsCount = mangaStats.collection_count + mangaStats.wishlist_count + mangaStats.read_count;

  return (
    <div className="pb-24 relative" data-testid="manga-detail-page">
      <Seo title={manga.title} description={manga.synopsis} image={manga.cover_url} path={`/manga/${manga.slug || id}`} type="book" />

      {/* Hero backdrop */}
      <div className="absolute top-0 left-0 right-0 h-[420px] overflow-hidden -z-10">
        {manga.cover_url && (
          <img src={manga.cover_url} alt="" className="w-full h-full object-cover object-center opacity-20 blur-2xl scale-110" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/40 via-[#09090B]/85 to-[#09090B]" />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white flex items-center gap-1"><House size={12} weight="bold" /> Início</Link>
          <CaretRight size={10} />
          <Link to="/database" className="hover:text-white">Mangás</Link>
          <CaretRight size={10} />
          <span className="text-zinc-300 truncate max-w-[240px]">{manga.title}</span>
          {user?.role === "admin" && (
            <Link to={`/admin?tab=manga&edit=${manga.manga_id}`} className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#7209B7]/40 text-[#c07be0] hover:bg-[#7209B7]/10 transition-colors" data-testid="admin-edit-manga-btn">
              <PencilSimple size={14} weight="bold" /> Editar mangá
            </Link>
          )}
        </div>

        <div className="grid lg:grid-cols-[280px_1fr_300px] gap-6 lg:gap-8">
          {/* ===== LEFT COLUMN ===== */}
          <aside className="space-y-4">
            <div className="aspect-[2/3] rounded-2xl overflow-hidden border border-white/10 bg-[#121217] shadow-2xl relative">
              {manga.cover_url ? (
                <img src={manga.cover_url} alt={manga.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-zinc-700 font-mono-accent">SEM CAPA</div>
              )}
            </div>

            <div className="space-y-2">
              {/* Add to list with dropdown */}
              <div className="relative">
                <div className="flex">
                  <Button onClick={() => toggleList("collection")} className={`flex-1 rounded-r-none ${status.in_collection ? "bg-[#7209B7] hover:bg-[#8B14D3]" : "btn-crimson"}`} data-testid="toggle-collection-btn">
                    <BookmarkSimple size={18} weight={status.in_collection ? "fill" : "bold"} className="mr-2" />
                    {status.in_collection ? "Na Coleção" : "Adicionar à Lista"}
                  </Button>
                  <button onClick={() => setListOpen(!listOpen)} className={`px-2.5 rounded-r-lg border-l border-black/20 ${status.in_collection ? "bg-[#7209B7] hover:bg-[#8B14D3]" : "bg-[#E63946] hover:bg-[#c92f3c]"} text-white`} data-testid="list-dropdown-btn">
                    <CaretDown size={16} weight="bold" className={`transition-transform ${listOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {listOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-[#121217] border border-white/10 rounded-lg overflow-hidden shadow-xl" data-testid="list-dropdown">
                    <ListOption active={status.in_collection} icon={<BookmarkSimple size={15} weight="fill" />} label="Coleção" onClick={() => toggleList("collection")} />
                    <ListOption active={status.in_wishlist} icon={<Clock size={15} weight="fill" />} label="Mais tarde" onClick={() => toggleList("wishlist")} />
                    <ListOption active={status.in_read} icon={<BookOpen size={15} weight="fill" />} label="Já lidos" onClick={() => toggleList("read")} />
                  </div>
                )}
              </div>
              <Button onClick={() => toggleList("read")} variant="outline" className={`w-full border-white/10 ${status.in_read ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : ""}`} data-testid="toggle-read-btn">
                <Check size={18} weight="bold" className="mr-2" /> {status.in_read ? "Já Lido" : "Já Lidos"}
              </Button>
              <Button onClick={() => toggleList("wishlist")} variant="outline" className={`w-full border-white/10 ${status.in_wishlist ? "bg-white/5 text-[#E63946]" : ""}`} data-testid="toggle-wishlist-btn">
                <Clock size={18} weight="bold" className="mr-2" /> {status.in_wishlist ? "Guardado" : "Mais tarde"}
              </Button>
              <ShareRow slug={manga.slug || id} title={manga.title} />
            </div>

            {/* Disponível em Portugal */}
            {manga.publisher && (
              <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-4" data-testid="publisher-card">
                <div className="font-mono-accent text-[10px] text-zinc-400 flex items-center gap-1.5 mb-3">
                  <SealCheck size={13} weight="fill" className="text-emerald-400" /> DISPONÍVEL EM PORTUGAL
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#7209B7] to-[#E63946] grid place-items-center flex-shrink-0">
                    <Buildings size={20} weight="fill" className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{manga.publisher}</div>
                    {totalVolumes && <div className="text-xs text-zinc-500">{totalVolumes} volumes</div>}
                  </div>
                  <Check size={18} weight="bold" className="text-emerald-400 ml-auto" />
                </div>
                <Link to={`/database?publisher=${encodeURIComponent(manga.publisher)}`} className="mt-3 flex items-center justify-between text-sm text-zinc-400 hover:text-white border-t border-white/5 pt-3" data-testid="see-publisher-link">
                  Ver editora <CaretRight size={14} weight="bold" />
                </Link>
              </div>
            )}
          </aside>

          {/* ===== CENTER COLUMN ===== */}
          <div className="min-w-0">
            <div className="font-mono-accent text-xs text-[#E63946] mb-2 flex items-center gap-1.5"><Books size={14} weight="bold" /> MANGÁ</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-none">{manga.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-300">
              {manga.author && <span className="inline-flex items-center gap-1.5"><UserIcon size={15} weight="bold" className="text-zinc-500" /> {manga.author}</span>}
              {manga.publisher && <span className="inline-flex items-center gap-1.5"><Buildings size={15} weight="bold" className="text-zinc-500" /> {manga.publisher}</span>}
              {totalVolumes && <span className="inline-flex items-center gap-1.5"><Books size={15} weight="bold" className="text-zinc-500" /> {totalVolumes} volumes</span>}
              {manga.status && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-mono-accent" data-testid="manga-status-badge">
                  <SealCheck size={13} weight="fill" /> {manga.status}
                </span>
              )}
            </div>

            {manga.genres?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {manga.genres.map((g) => (
                  <Link key={g} to={`/database?q=${encodeURIComponent(g)}`} className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs hover:border-[#E63946]/60 hover:text-white transition-colors" data-testid={`manga-genre-${g}`}>{g}</Link>
                ))}
              </div>
            )}

            {/* Rating + stats bar */}
            <div className="mt-6 grid sm:grid-cols-[auto_1fr] gap-4 items-stretch">
              <div className="bg-[#121217] border border-white/5 rounded-2xl p-5 flex items-center gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Star size={24} weight="fill" className="text-[#F59E0B]" />
                    <span className="font-display text-3xl font-extrabold">{rating.average.toFixed(1)}</span>
                    <span className="text-zinc-500 text-sm">/10</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">({rating.count} {rating.count === 1 ? "voto" : "votos"})</div>
                  <StarsDisplay value={rating.average} />
                </div>
                <Button onClick={() => { setTab("comentarios"); setTimeout(() => document.getElementById("manga-tabs-content")?.scrollIntoView({ behavior: "smooth" }), 80); }} variant="outline" className="border-[#E63946]/40 text-white hover:bg-[#E63946]/10" data-testid="avaliar-btn">
                  <Star size={16} weight="bold" className="mr-1.5" /> Avaliar
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <StatCard icon={<ListChecks size={18} weight="fill" />} value={listsCount} label="Listas" />
                <StatCard icon={<Eye size={18} weight="fill" />} value={manga.view_count || 0} label="Leituras" />
                <StatCard icon={<BookmarkSimple size={18} weight="fill" />} value={mangaStats.collection_count} label="Na coleção" />
              </div>
            </div>

            {/* Sinopse */}
            {manga.synopsis && (
              <div className="mt-6 bg-[#121217] border border-white/5 rounded-2xl p-6">
                <div className="font-display font-bold text-lg flex items-center gap-2 mb-3"><Books size={18} weight="fill" className="text-[#E63946]" /> Sinopse</div>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-line text-sm">{manga.synopsis}</p>
              </div>
            )}

            {/* Video (logo abaixo da sinopse) */}
            {embed && (
              <div className="mt-6">
                <div className="font-mono-accent text-xs text-zinc-500 mb-3 flex items-center gap-2"><PlayCircle size={16} weight="bold" className="text-[#E63946]" /> VÍDEO DE APRESENTAÇÃO</div>
                <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black">
                  <iframe src={embed} title={manga.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="mt-6 border-b border-white/5 flex items-center gap-1" data-testid="manga-tabs">
              <TabBtn active={tab === "volumes"} onClick={() => setTab("volumes")} icon={<Stack size={16} weight="bold" />} label="Volumes" testid="tab-volumes" />
              <TabBtn active={tab === "info"} onClick={() => setTab("info")} icon={<Info size={16} weight="bold" />} label="Informações" testid="tab-info" />
              <TabBtn active={tab === "comentarios"} onClick={() => setTab("comentarios")} icon={<ChatCircleText size={16} weight="bold" />} label={`Comentários${commentCount ? ` ${commentCount}` : ""}`} testid="tab-comentarios" />
            </div>

            <div id="manga-tabs-content" className="mt-5">
              {tab === "volumes" && (
                series && !series.single
                  ? <SeriesVolumes identifier={manga.slug || id} />
                  : <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center text-sm text-zinc-500">Este mangá não faz parte de uma série com vários volumes.</div>
              )}
              {tab === "info" && <InfoTab manga={manga} totalVolumes={totalVolumes} />}
              {tab === "comentarios" && <MangaRatings mangaId={id} />}
            </div>
          </div>

          {/* ===== RIGHT SIDEBAR ===== */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-5" data-testid="about-card">
              <div className="font-display font-bold flex items-center gap-2 mb-4"><Books size={18} weight="fill" className="text-[#E63946]" /> Sobre o Mangá</div>
              <AboutRow icon={<Books size={15} weight="bold" />} label="Volumes" value={totalVolumes || "—"} />
              {manga.status && <AboutRow icon={<SealCheck size={15} weight="bold" />} label="Status" value={manga.status} />}
              {manga.release_year && <AboutRow icon={<Clock size={15} weight="bold" />} label="Ano" value={manga.release_year} />}
              {manga.genres?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" /> Géneros</div>
                  <div className="flex flex-wrap gap-1.5">
                    {manga.genres.map(g => (
                      <Link key={g} to={`/database?q=${encodeURIComponent(g)}`} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] hover:border-[#E63946]/50 transition-colors">{g}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {otherByAuthor.length > 0 && (
              <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-5" data-testid="other-by-author-card">
                <div className="font-display font-bold flex items-center gap-2 mb-4"><UserIcon size={18} weight="fill" className="text-[#7209B7]" /> Outros do Autor</div>
                <div className="space-y-3">
                  {otherByAuthor.map(m => (
                    <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="flex items-center gap-3 group" data-testid={`other-author-${m.manga_id}`}>
                      <div className="w-11 h-16 rounded-md overflow-hidden bg-[#121217] flex-shrink-0 border border-white/5">
                        {m.cover_url ? <img src={m.cover_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><Books size={16} /></div>}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-sm leading-tight line-clamp-2 group-hover:text-[#E63946] transition-colors">{m.title}</div>
                        {m.publisher && <div className="text-[11px] text-zinc-500 mt-0.5">{m.publisher}</div>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {manga.purchase_links?.length > 0 && (
              <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-5" data-testid="purchase-card">
                <div className="font-display font-bold flex items-center gap-2 mb-4"><ShoppingCartSimple size={18} weight="fill" className="text-[#E63946]" /> Onde Comprar</div>
                <div className="space-y-2">
                  {manga.purchase_links.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`purchase-link-${i}`} className="flex items-center justify-between bg-[#121217] border border-white/10 rounded-lg px-4 py-2.5 hover:border-[#E63946]/50 transition-colors">
                      <span className="font-display font-semibold text-sm">{p.store}</span>
                      <ShoppingCartSimple size={16} weight="bold" className="text-[#E63946]" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function ListOption({ active, icon, label, onClick }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors ${active ? "text-emerald-400" : "text-zinc-300 hover:bg-white/5"}`}>
      {icon} {label} {active && <Check size={14} weight="bold" className="ml-auto" />}
    </button>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-[#121217] border border-white/5 rounded-xl p-3 flex flex-col items-center justify-center text-center">
      <div className="text-zinc-400 mb-1">{icon}</div>
      <div className="font-display font-extrabold text-lg leading-none">{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1 font-mono-accent uppercase">{label}</div>
    </div>
  );
}

function StarsDisplay({ value }) {
  const filled = Math.round(value / 2);
  return (
    <div className="flex items-center gap-0.5 mt-1.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={14} weight="fill" className={s <= filled ? "text-[#F59E0B]" : "text-zinc-700"} />
      ))}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-2 px-4 py-3 text-sm font-display font-semibold border-b-2 transition-colors ${active ? "text-white border-[#E63946]" : "text-zinc-400 border-transparent hover:text-white"}`}>
      {icon} {label}
    </button>
  );
}

function AboutRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-zinc-400 flex items-center gap-2">{icon} {label}</span>
      <span className="font-display font-semibold">{value}</span>
    </div>
  );
}

function InfoTab({ manga, totalVolumes }) {
  const rows = [
    ["Autor", manga.author],
    ["Editora", manga.publisher],
    ["Volumes", totalVolumes],
    ["Estado", manga.status],
    ["ISBN", manga.isbn],
    ["Páginas", manga.pages],
    ["Ano", manga.release_year],
  ].filter(([, v]) => v);
  return (
    <div className="bg-[#121217] border border-white/5 rounded-2xl divide-y divide-white/5" data-testid="info-tab">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between px-5 py-3 text-sm">
          <span className="text-zinc-500">{k}</span>
          <span className="font-display font-semibold text-right">{v}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">Sem informação adicional.</div>}
    </div>
  );
}

function ShareRow({ slug, title }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/manga/${slug}`;
  const text = `${title} · PortugalManga`;
  const links = [
    { label: "WhatsApp", icon: WhatsappLogo, color: "hover:text-[#25D366]", href: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}` },
    { label: "X", icon: XLogo, color: "hover:text-white", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}` },
    { label: "Facebook", icon: FacebookLogo, color: "hover:text-[#1877F2]", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
  ];
  function copy() {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); toast.success("Link copiado"); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-2" data-testid="share-buttons">
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 flex items-center gap-1.5 pl-1"><ShareNetwork size={14} weight="bold" /> Partilhar</span>
        <div className="flex items-center gap-1 ml-auto">
          {links.map((l) => (
            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" title={l.label} className={`w-8 h-8 rounded-md grid place-items-center text-zinc-300 transition-colors ${l.color}`} data-testid={`share-${l.label.toLowerCase()}`}>
              <l.icon size={17} weight="fill" />
            </a>
          ))}
          <button onClick={copy} title="Copiar link" className="w-8 h-8 rounded-md grid place-items-center text-zinc-300 hover:text-white transition-colors" data-testid="share-copy">
            {copied ? <Check size={17} weight="bold" className="text-emerald-400" /> : <LinkSimple size={17} weight="bold" />}
          </button>
        </div>
      </div>
    </div>
  );
}


function LockedManga({ detail }) {
  const publishAt = detail?.publish_at;
  const dateObj = publishAt ? new Date(publishAt) : null;
  const dateStr = dateObj ? dateObj.toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" }) : null;
  const timeStr = dateObj ? dateObj.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : null;

  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    if (!dateObj) return;
    const tick = () => {
      const diff = dateObj.getTime() - Date.now();
      if (diff <= 0) { setRemaining(""); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [publishAt]);

  return (
    <div className="min-h-screen pt-24 pb-24 flex items-center justify-center px-5" data-testid="manga-locked-page">
      <Seo title={detail?.title ? `${detail.title} — Em breve` : "Em breve"} description="Este mangá ainda não foi lançado." />
      <div className="max-w-lg w-full text-center bg-[#121217] border border-white/10 rounded-3xl p-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E63946]/10 to-transparent pointer-events-none" />
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-[#E63946]/15 border border-[#E63946]/30 grid place-items-center mx-auto mb-6">
            <Clock size={38} weight="fill" className="text-[#E63946]" />
          </div>
          <div className="font-mono-accent text-[11px] tracking-widest uppercase text-[#E63946] mb-2" data-testid="locked-badge">Ainda não foi lançado</div>
          {detail?.title && <h1 className="font-display text-3xl font-extrabold mb-4">{detail.title}</h1>}
          {dateObj ? (
            <>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Este mangá ficará disponível a partir de:
              </p>
              <div className="rounded-2xl bg-black/30 border border-white/10 p-5 mb-6" data-testid="locked-datetime">
                <div className="font-display text-2xl font-bold">{dateStr}</div>
                <div className="text-[#E63946] font-mono-accent mt-1">às {timeStr}</div>
              </div>
              {remaining && (
                <div className="text-sm text-zinc-400 mb-6" data-testid="locked-countdown">
                  Faltam <span className="text-white font-semibold font-mono-accent">{remaining}</span>
                </div>
              )}
            </>
          ) : (
            <p className="text-zinc-400 text-sm leading-relaxed mb-6" data-testid="locked-nodate">
              Este mangá ainda não está disponível. Volta mais tarde!
            </p>
          )}
          <Link to="/database" className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg btn-crimson font-medium" data-testid="locked-back-btn">
            <Books size={16} weight="bold" /> Explorar outros mangás
          </Link>
        </div>
      </div>
    </div>
  );
}
